/**
 * React Query hook dla Existing Posts Awareness panel.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { syncAndAssessForMap, assessQualityForWebsite } from "@/services/wpPostsSyncService";
import { extractSroForWebsite } from "@/services/wpSroExtractor";
import { auditQualityForWebsite } from "@/services/wpQualityAuditor";
import { recomputeMatchStatusAfterAudit } from "@/services/existingPostMatcher";
import { syncSeoDataForWebsite } from "@/services/senutoSyncService";
import { enqueueSenutoSync } from "@/services/queueClient";
import { isWorkerModeMapEnabled } from "@/lib/workerMode";
import {
  matchNodesToWpPosts,
  getMatchingSummary,
  approveRefurbish,
  bulkApproveRefurbish,
  autoApproveRefurbishBelowThreshold,
  updateNodeMatchStatus,
  recomputeClusterFitAfterMatching,
} from "@/services/existingPostMatcher";
import {
  rollbackRefurbish,
  getRefurbishHistory,
} from "@/services/wpPostRefurbishService";
import {
  classifyClusterFitForMap,
  setClusterFitManualOverride,
  type ClusterFitLabel,
  type ClusterFitMethod,
} from "@/services/wpClusterFitClassifier";
import {
  recomputeFinalActionsForWebsite,
  acceptFinalAction,
  overrideFinalAction,
  unlockFinalAction,
  bulkAcceptByAction,
  type FinalAction,
  type FinalActionPriority,
} from "@/services/decisionEngine";
import { processWpActionsForWebsite } from "@/services/wpActionProcessor";
import { exportWpDataAsJson } from "@/services/exportService";
import type {
  MatchStatus,
  QualityRecommendation,
} from "@/types/thematicMaps";

export interface WpPostRow {
  id: string;
  wp_post_id: number;
  title: string;
  slug: string | null;
  status: string | null; // WP status: 'publish' | 'draft' | 'trash' | 'private' | 'pending'
  action_executed_at: string | null; // Timestamp ostatniego pomyślnego wykonania akcji (delete/keep_outer/monitor)
  wp_url: string | null;
  published_at: string | null;
  modified_at: string | null;
  word_count: number | null;
  h2_count: number | null;
  h3_count: number | null;
  quality_score: number | null;
  quality_recommendation: QualityRecommendation | null;
  quality_red_flags: string[];
  excerpt: string | null;
  meta_description: string | null;
  // M8/M9 SRO
  sro_extracted_at: string | null;
  extracted_entity: string | null;
  extracted_attribute: string | null;
  extracted_query_frame: string | null;
  first_paragraph_answer: string | null;
  supporting_entities: string[];
  // M12 Quality Audit LLM
  entity_anchoring_score: number | null;
  information_gain_score: number | null;
  humanization_risk_score: number | null;
  retrieval_ready: boolean | null;
  quality_audit_issues: string[];
  quality_audit_verdict: string | null;
  quality_audited_at: string | null;
  // M13 SEO signals (Senuto)
  seo_organic_traffic: number | null;
  seo_total_keywords: number | null;
  seo_top_3_count: number | null;
  seo_top_10_count: number | null;
  seo_top_50_count: number | null;
  seo_best_keyword: string | null;
  seo_best_position: number | null;
  seo_gained_keywords_count: number | null;
  seo_lost_keywords_count: number | null;
  seo_content_decay: boolean | null;
  seo_has_cannibalization: boolean | null;
  seo_cannibalization_partner_url: string | null;
  seo_last_sync: string | null;
  // M14 Cluster Fit (scope classification)
  cluster_fit: ClusterFitLabel | null;
  cluster_fit_confidence: number | null;
  cluster_fit_reason: string | null;
  cluster_fit_classified_at: string | null;
  cluster_fit_method: ClusterFitMethod | null;
  // M15 Decision Engine (final_action spina 4 wymiary)
  final_action: FinalAction | null;
  final_action_priority: FinalActionPriority | null;
  final_action_reason: string | null;
  final_action_computed_at: string | null;
  final_action_locked: boolean | null;
  final_action_user_override: boolean | null;
}

interface MatchRow {
  id: string;
  title: string;
  slug: string;
  seed_id: string;
  match_status: MatchStatus | null;
  match_confidence: number | null;
  match_reason: string | null;
  matched_wp_post_id: string | null;
  refurbish_approved_by_user: boolean | null;
  existing_wp_quality_score: number | null;
  existing_wp_word_count: number | null;
  // joined
  wp_post_title?: string;
  wp_post_url?: string | null;
  wp_post_quality_recommendation?: string | null;
  wp_post_quality_red_flags?: string[];
}

async function getMatchingResults(mapId: string): Promise<MatchRow[]> {
  const { data, error } = await supabase
    .from("thematic_nodes")
    .select(
      `id, title, slug, seed_id, match_status, match_confidence, match_reason,
       matched_wp_post_id, refurbish_approved_by_user, existing_wp_quality_score,
       existing_wp_word_count,
       wp_post:wp_posts!matched_wp_post_id(title, wp_url, quality_recommendation, quality_red_flags)`,
    )
    .eq("map_id", mapId)
    .order("publish_priority", { ascending: false });

  if (error) throw error;

  return ((data || []) as Array<
    MatchRow & {
      wp_post?: {
        title: string;
        wp_url: string | null;
        quality_recommendation: string | null;
        quality_red_flags: string[];
      } | null;
    }
  >).map((r) => ({
    ...r,
    wp_post_title: r.wp_post?.title,
    wp_post_url: r.wp_post?.wp_url ?? null,
    wp_post_quality_recommendation: r.wp_post?.quality_recommendation ?? null,
    wp_post_quality_red_flags: r.wp_post?.quality_red_flags || [],
  }));
}

async function getWpPostsForMap(mapId: string): Promise<{
  websiteId: string | null;
  posts: WpPostRow[];
}> {
  const { data: map } = await supabase
    .from("thematic_maps")
    .select("website_id")
    .eq("id", mapId)
    .maybeSingle();
  const websiteId = (map as { website_id: string | null } | null)?.website_id || null;
  if (!websiteId) return { websiteId: null, posts: [] };

  const { data, error } = await supabase
    .from("wp_posts")
    .select(
      "id, wp_post_id, title, slug, status, action_executed_at, wp_url, published_at, modified_at, word_count, h2_count, h3_count, quality_score, quality_recommendation, quality_red_flags, excerpt, meta_description, sro_extracted_at, extracted_entity, extracted_attribute, extracted_query_frame, first_paragraph_answer, supporting_entities, entity_anchoring_score, information_gain_score, humanization_risk_score, retrieval_ready, quality_audit_issues, quality_audit_verdict, quality_audited_at, seo_organic_traffic, seo_total_keywords, seo_top_3_count, seo_top_10_count, seo_top_50_count, seo_best_keyword, seo_best_position, seo_gained_keywords_count, seo_lost_keywords_count, seo_content_decay, seo_has_cannibalization, seo_cannibalization_partner_url, seo_last_sync, cluster_fit, cluster_fit_confidence, cluster_fit_reason, cluster_fit_classified_at, cluster_fit_method, final_action, final_action_priority, final_action_reason, final_action_computed_at, final_action_locked, final_action_user_override",
    )
    .eq("website_id", websiteId)
    .order("published_at", { ascending: false });

  if (error) throw error;
  return {
    websiteId,
    posts: ((data || []) as Array<
      WpPostRow & {
        quality_red_flags: unknown;
        supporting_entities: unknown;
        quality_audit_issues: unknown;
      }
    >).map((r) => ({
      ...r,
      quality_red_flags: Array.isArray(r.quality_red_flags) ? (r.quality_red_flags as string[]) : [],
      supporting_entities: Array.isArray(r.supporting_entities) ? (r.supporting_entities as string[]) : [],
      quality_audit_issues: Array.isArray(r.quality_audit_issues) ? (r.quality_audit_issues as string[]) : [],
    })),
  };
}

async function getOtherMapsUsingWebsite(mapId: string, websiteId: string): Promise<number> {
  const { count } = await supabase
    .from("thematic_maps")
    .select("id", { count: "exact", head: true })
    .eq("website_id", websiteId)
    .neq("id", mapId);
  return count ?? 0;
}

/**
 * Reset debug — wraca feature do stanu czystego dla tej mapy:
 *  1) thematic_nodes.match_* i refurbish_* wyzerowane (match_status='unchecked')
 *  2) thematic_maps.wp_posts_synced_at=NULL, wp_posts_count=0
 *  3) DELETE wp_matching_runs dla tej mapy
 *  4) DELETE wp_posts dla domeny (nastepny sync pobierze od nowa)
 *
 * NIE kasuje nodow, NIE kasuje wpisow na WordPressie, NIE kasuje wp_post_refurbish_backups.
 */
async function resetDebugState(params: { mapId: string; websiteId: string }): Promise<void> {
  const { mapId, websiteId } = params;

  // 1. Reset match/refurbish fields on nodes
  {
    const { error } = await supabase
      .from("thematic_nodes")
      .update({
        match_status: "unchecked",
        matched_wp_post_id: null,
        match_confidence: null,
        match_reason: null,
        match_method: null,
        matched_at: null,
        refurbish_approved_by_user: false,
        refurbish_approved_at: null,
        refurbish_approved_by: null,
        refurbished_at: null,
        refurbish_reason: null,
        existing_wp_quality_score: null,
        existing_wp_word_count: null,
      })
      .eq("map_id", mapId);
    if (error) throw new Error(`Reset nodes: ${error.message}`);
  }

  // 2. Reset map sync meta
  {
    const { error } = await supabase
      .from("thematic_maps")
      .update({
        wp_posts_synced_at: null,
        wp_posts_count: 0,
      })
      .eq("id", mapId);
    if (error) throw new Error(`Reset map: ${error.message}`);
  }

  // 3. Delete matching runs for this map
  {
    const { error } = await supabase.from("wp_matching_runs").delete().eq("map_id", mapId);
    if (error) throw new Error(`Delete matching runs: ${error.message}`);
  }

  // 4. Delete wp_posts for this website
  {
    const { error } = await supabase.from("wp_posts").delete().eq("website_id", websiteId);
    if (error) throw new Error(`Delete wp_posts: ${error.message}`);
  }
}

export function useExistingPosts(mapId: string) {
  const qc = useQueryClient();

  const summary = useQuery({
    queryKey: ["existing-posts", mapId, "summary"],
    queryFn: () => getMatchingSummary(mapId),
    enabled: !!mapId,
  });

  const wpPosts = useQuery({
    queryKey: ["existing-posts", mapId, "wp-posts"],
    queryFn: () => getWpPostsForMap(mapId),
    enabled: !!mapId,
  });

  const results = useQuery({
    queryKey: ["existing-posts", mapId, "results"],
    queryFn: () => getMatchingResults(mapId),
    enabled: !!mapId,
  });

  const history = useQuery({
    queryKey: ["existing-posts", mapId, "history"],
    queryFn: () => getRefurbishHistory(mapId),
    enabled: !!mapId,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["existing-posts", mapId] });
  };

  const syncMutation = useMutation({
    mutationFn: async (opts: {
      userId: string;
      useLlmCheck?: boolean;
      onLog?: (type: "info" | "success" | "error" | "stage", message: string, debug?: Record<string, unknown>) => void;
    }) => syncAndAssessForMap(mapId, opts),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ["thematic-maps"] });
    },
  });

  const matchMutation = useMutation({
    mutationFn: async (opts: {
      triggeredBy?: string | null;
      onLog?: (type: "info" | "success" | "error" | "stage", message: string, debug?: Record<string, unknown>) => void;
    }) => {
      // Hardcoded 'deep' mode — L1 hash -> L2 Jaccard -> L3 LLM confirm (zawsze pelny pipeline)
      const result = await matchNodesToWpPosts(mapId, { ...opts, mode: "deep" });
      // Auto-invoke recompute cluster_fit po matchingu
      try {
        const { data: mapRow } = await supabase
          .from("thematic_maps")
          .select("website_id")
          .eq("id", mapId)
          .maybeSingle();
        const websiteId = (mapRow as { website_id: string | null } | null)?.website_id;
        if (websiteId) {
          opts.onLog?.("info", "▶ Recompute cluster_fit po matchingu...");
          const rec = await recomputeClusterFitAfterMatching(mapId, websiteId, opts.onLog);
          opts.onLog?.(
            "success",
            `✓ Recompute cluster_fit: ${rec.promoted_to_in_scope} promoted, ${rec.demoted_for_reclassify} demoted`,
            { recompute: rec },
          );
        }
      } catch (err) {
        opts.onLog?.("error", `Recompute cluster_fit failed: ${err instanceof Error ? err.message : String(err)}`);
      }
      return result;
    },
    onSuccess: invalidate,
  });

  const extractSroMutation = useMutation({
    mutationFn: async (opts: {
      websiteId: string;
      onLog?: (type: "info" | "success" | "error" | "stage", message: string, debug?: Record<string, unknown>) => void;
      onlyMissing?: boolean;
    }) =>
      extractSroForWebsite(opts.websiteId, {
        onLog: opts.onLog,
        onlyMissing: opts.onlyMissing,
      }),
    onSuccess: invalidate,
  });

  const syncSeoMutation = useMutation({
    mutationFn: async (opts: {
      websiteId: string;
      onLog?: (type: "info" | "success" | "error" | "stage", message: string, debug?: Record<string, unknown>) => void;
    }) => {
      // Worker mode: enqueue + return od razu (UI subskrybuje realtime).
      // Rozwiazuje browser CORS do api.senuto.com — Node fetch bez CORS.
      if (await isWorkerModeMapEnabled()) {
        opts.onLog?.("stage", "▶ Worker: Senuto sync w tle (zamknij karte spokojnie)");
        const res = await enqueueSenutoSync(opts.websiteId);
        opts.onLog?.("success", `Job #${res.queueRowId.slice(0, 8)} enqueued — postęp w generation_queue`);
        // Zwroc pusty wynik — UI subskrybuje realtime dla step=senuto_sync
        return {
          total_posts: 0, posts_with_rankings: 0, total_keywords: 0,
          total_cannibalization_pairs: 0, duration_ms: 0,
          domain_visibility: null, domain_top_10: null,
          _viaWorker: true, _queueRowId: res.queueRowId,
        };
      }
      return syncSeoDataForWebsite(opts.websiteId, {
        onLog: (msg) => opts.onLog?.("info", msg),
      });
    },
    onSuccess: invalidate,
  });

  const auditQualityMutation = useMutation({
    mutationFn: async (opts: {
      websiteId: string;
      onLog?: (type: "info" | "success" | "error" | "stage", message: string, debug?: Record<string, unknown>) => void;
      forceReaudit?: boolean;
      concurrency?: number;
    }) => {
      // Faza 0 (baseline): heurystyka (zero kosztu, zero LLM) — zapisuje quality_* na kazdym
      // wpisie ktory jej jeszcze nie ma. LLM audit ponizej nadpisuje quality_recommendation
      // na wpisach majacych SRO, ale heurystyka zostaje jako fallback dla reszty.
      opts.onLog?.("info", "▶ Faza 1/2: Heurystyczna ocena (baseline, 6 reguł)");
      const baseline = await assessQualityForWebsite(opts.websiteId, {
        onLog: opts.onLog,
        force: opts.forceReaudit,
      });
      opts.onLog?.(
        "stage",
        `  ✓ Heurystyka: keep=${baseline.keep_count} borderline=${baseline.borderline_count} refurbish=${baseline.refurbish_count}`,
      );

      opts.onLog?.("info", "▶ Faza 2/2: LLM audit SRO (gpt-4.1-mini, 4 wymiary)");
      const stats = await auditQualityForWebsite(opts.websiteId, {
        onLog: opts.onLog,
        forceReaudit: opts.forceReaudit,
        concurrency: opts.concurrency ?? 20, // domyślny szybszy audit (było 5, limit 30)
      });
      // Auto-invoke recompute match_status na mapach tego website
      if (stats.succeeded > 0) {
        opts.onLog?.("info", "▶ Recompute match_status po audycie...");
        const rec = await recomputeMatchStatusAfterAudit(opts.websiteId, opts.onLog);
        opts.onLog?.(
          "success",
          `✓ Recompute: ${rec.updated} nodów zaktualizowanych, ${rec.unchanged} bez zmian`,
          { recompute: rec },
        );
      }
      return stats;
    },
    onSuccess: invalidate,
  });

  const classifyClusterFitMutation = useMutation({
    mutationFn: async (opts: {
      onLog?: (type: "info" | "success" | "error" | "stage", message: string, debug?: Record<string, unknown>) => void;
      forceReclassify?: boolean;
      concurrency?: number;
    }) =>
      classifyClusterFitForMap(mapId, {
        onLog: opts.onLog,
        forceReclassify: opts.forceReclassify,
        concurrency: opts.concurrency ?? 10,
      }),
    onSuccess: invalidate,
  });

  const changeClusterFitMutation = useMutation({
    mutationFn: async (opts: {
      wpPostId: string;
      newValue: ClusterFitLabel;
      reason?: string;
    }) => setClusterFitManualOverride(opts.wpPostId, opts.newValue, opts.reason),
    onSuccess: invalidate,
  });

  // M15 — Decision Engine mutations
  const computeFinalActionMutation = useMutation({
    mutationFn: async (opts: {
      websiteId: string;
      onLog?: (type: "info" | "success" | "error" | "stage", message: string, debug?: Record<string, unknown>) => void;
      forceUnlocked?: boolean;
    }) =>
      recomputeFinalActionsForWebsite(opts.websiteId, {
        onLog: opts.onLog,
        forceUnlocked: opts.forceUnlocked,
      }),
    onSuccess: invalidate,
  });

  const acceptFinalActionMutation = useMutation({
    mutationFn: async (opts: { wpPostId: string; userId?: string | null }) =>
      acceptFinalAction(opts.wpPostId, opts.userId),
    onSuccess: invalidate,
  });

  const overrideFinalActionMutation = useMutation({
    mutationFn: async (opts: {
      wpPostId: string;
      newAction: FinalAction;
      reason?: string;
      userId?: string | null;
    }) => overrideFinalAction(opts.wpPostId, opts.newAction, opts.reason, opts.userId),
    onSuccess: invalidate,
  });

  const unlockFinalActionMutation = useMutation({
    mutationFn: async (opts: { wpPostId: string }) => unlockFinalAction(opts.wpPostId),
    onSuccess: invalidate,
  });

  const bulkAcceptByActionMutation = useMutation({
    mutationFn: async (opts: {
      websiteId: string;
      action: FinalAction;
      userId?: string | null;
    }) => bulkAcceptByAction(opts.websiteId, opts.action, opts.userId),
    onSuccess: invalidate,
  });

  const processActionsMutation = useMutation({
    mutationFn: async (opts: {
      websiteId: string;
      userId?: string | null;
      onLog?: (type: "info" | "success" | "error" | "stage", message: string, debug?: Record<string, unknown>) => void;
    }) =>
      processWpActionsForWebsite(opts.websiteId, {
        userId: opts.userId,
        onLog: opts.onLog,
      }),
    onSuccess: invalidate,
  });

  const exportJsonMutation = useMutation({
    mutationFn: async (opts: { onLog?: (msg: string) => void }) =>
      exportWpDataAsJson(mapId, { onLog: opts.onLog }),
  });

  const approveMutation = useMutation({
    mutationFn: async (opts: { nodeId: string; userId?: string | null }) =>
      approveRefurbish(opts.nodeId, opts.userId),
    onSuccess: invalidate,
  });

  const bulkApproveMutation = useMutation({
    mutationFn: async (opts: { nodeIds: string[]; userId?: string | null }) =>
      bulkApproveRefurbish(opts.nodeIds, opts.userId),
    onSuccess: invalidate,
  });

  const autoApproveMutation = useMutation({
    mutationFn: async (opts: { threshold: number; userId?: string | null }) =>
      autoApproveRefurbishBelowThreshold(mapId, opts.threshold, opts.userId),
    onSuccess: invalidate,
  });

  const changeStatusMutation = useMutation({
    mutationFn: async (opts: { nodeId: string; status: MatchStatus; reason?: string }) =>
      updateNodeMatchStatus(opts.nodeId, opts.status, opts.reason),
    onSuccess: invalidate,
  });

  const rollbackMutation = useMutation({
    mutationFn: async (opts: { backupId: string; userId?: string | null }) =>
      rollbackRefurbish(opts.backupId, opts.userId),
    onSuccess: invalidate,
  });

  const resetDebugMutation = useMutation({
    mutationFn: async (opts: { websiteId: string }) =>
      resetDebugState({ mapId, websiteId: opts.websiteId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["existing-posts", mapId] });
      qc.invalidateQueries({ queryKey: ["map-website", mapId] });
      qc.invalidateQueries({ queryKey: ["thematic-maps"] });
      qc.invalidateQueries({ queryKey: ["wp-posts"] });
    },
  });

  return {
    summary,
    results,
    history,
    wpPosts,
    sync: syncMutation,
    match: matchMutation,
    extractSro: extractSroMutation,
    syncSeo: syncSeoMutation,
    auditQuality: auditQualityMutation,
    classifyClusterFit: classifyClusterFitMutation,
    changeClusterFit: changeClusterFitMutation,
    computeFinalAction: computeFinalActionMutation,
    acceptFinalAction: acceptFinalActionMutation,
    overrideFinalAction: overrideFinalActionMutation,
    unlockFinalAction: unlockFinalActionMutation,
    bulkAcceptByAction: bulkAcceptByActionMutation,
    processActions: processActionsMutation,
    exportJson: exportJsonMutation,
    approveRefurbish: approveMutation,
    bulkApprove: bulkApproveMutation,
    autoApprove: autoApproveMutation,
    changeStatus: changeStatusMutation,
    rollback: rollbackMutation,
    resetDebug: resetDebugMutation,
    getOtherMapsUsingWebsite,
  };
}

export type { MatchRow };
